/**
 * The two audit surfaces (#2005).
 *
 * Each is `ResourcePage` over its own ledger with the shared strip injected
 * into the masthead via `host.listTabs`, and neither page wraps `ResourcePage`
 * in a container of its own.
 *
 * The `listTabs` override lives HERE rather than on the two resource
 * definitions because it is a fact about the PAGE (which strip surrounds this
 * list), not about the ledger. A resource that someone later mounts somewhere
 * else should not drag `/audit`'s navigation along with it.
 */
import { ResourcePage } from '@resource-kit/ui/react';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import type { AnyFieldDef } from '@resource-kit/model';
import { auditResource } from './auditResource';
import { resourceAuditResource } from './resourceAuditResource';
import { AuditTabs } from './AuditTabs';

/**
 * Wrap a ledger with the section title and the strip.
 *
 * `listTitle` is overridden to the SECTION name for both tabs, so the title
 * holds still while the strip says which half is showing. Before this the page
 * was titled after the active tab, so nothing on screen ever named the thing
 * the two tabs are two halves of.
 */
function auditSurface<T, F extends Record<string, AnyFieldDef>>(bundle: ResourceBundle<T, F>) {
  return (
    <ResourcePage
      bundle={{
        ...bundle,
        host: {
          ...bundle.host,
          listTitle: 'audit.pageTitle',
          listTabs: () => <AuditTabs />,
        },
      }}
    />
  );
}

/** `/audit/operations` — who called which privileged API. */
export function AuditOperationsPage() {
  return auditSurface(auditResource);
}

/** `/audit/changes` — which field of which record became what. */
export function AuditChangesPage() {
  return auditSurface(resourceAuditResource);
}
